// Skill tree: permanent nodes bought with skill points (SP). Persist across ascension.
import type { AddStat, MultStat } from '../game/stats';

export type BranchId = 'knight' | 'warlord' | 'sorcerer' | 'rogue';

export type NodeEffect =
  | { kind: 'mult'; stat: MultStat; perLevel: number } // 1 + perLevel * level
  | { kind: 'div'; stat: MultStat; perLevel: number }  // 1 / (1 + perLevel * level)
  | { kind: 'add'; stat: AddStat; perLevel: number };  // perLevel * level

export interface TreeNode {
  id: string;
  branch: BranchId;
  tier: 1 | 2 | 3;
  name: string;
  icon: string;
  /** `{v}` is replaced with the formatted value. */
  desc: string;
  effect: NodeEffect;
  maxLevel: number;
}

export interface BranchDef { id: BranchId; name: string; icon: string; color: string; blurb: string }

export const BRANCHES: readonly BranchDef[] = [
  { id: 'knight',   name: 'Hero',     icon: '⚔️', color: '#ff7a45', blurb: 'Tap damage and crits' },
  { id: 'warlord',  name: 'Captain',  icon: '🚩', color: '#58d68d', blurb: 'Guardian damage' },
  { id: 'sorcerer', name: 'Wizard',   icon: '🪄', color: '#66b3ff', blurb: 'Energy and Specials' },
  { id: 'rogue',    name: 'Treasure Hunter', icon: '🗺️', color: '#ffcc4d', blurb: 'Coins, Stardust and Giants' },
];

/** SP cost per level = tier. A tier opens once this many SP are spent in the branch. */
export const TIER_REQUIREMENT: Record<1 | 2 | 3, number> = { 1: 0, 2: 5, 3: 15 };

/** Skill points: 1 per `stagesPerPoint` of best stage, plus 1 per ascension (capped). */
export const SKILL_POINTS = { stagesPerPoint: 25, perAscension: 1, ascensionCap: 100 } as const;

const M = (stat: MultStat, perLevel: number): NodeEffect => ({ kind: 'mult', stat, perLevel });
const A = (stat: AddStat, perLevel: number): NodeEffect => ({ kind: 'add', stat, perLevel });

export const TREE: readonly TreeNode[] = [
  // Knight
  { id: 'k_edge',    branch: 'knight', tier: 1, name: 'Sharp Blade', icon: '🗡️', desc: 'Tap damage {v}', effect: M('tapDamage', 0.5), maxLevel: 10 },
  { id: 'k_eye',     branch: 'knight', tier: 1, name: 'Eagle Eye',       icon: '🦅', desc: 'Crit chance {v}', effect: A('critChance', 0.01), maxLevel: 10 },
  { id: 'k_brutal',  branch: 'knight', tier: 2, name: 'Big Hits',   icon: '💥', desc: 'Crit damage {v}', effect: M('critDamage', 0.3), maxLevel: 10 },
  { id: 'k_echo',    branch: 'knight', tier: 2, name: 'Echo Tap',  icon: '🔔', desc: 'Guardian DPS added to taps {v}', effect: A('tapDpsShare', 0.005), maxLevel: 10 },
  { id: 'k_breaker', branch: 'knight', tier: 3, name: 'Giant Slayer',  icon: '🪓', desc: 'Giant damage {v}', effect: M('bossDamage', 0.25), maxLevel: 10 },
  { id: 'k_flame',   branch: 'knight', tier: 3, name: 'Hot Sword',  icon: '🔥', desc: 'Slash power {v}', effect: A('fireswordBonus', 0.5), maxLevel: 10 },
  // Warlord
  { id: 'w_rally',    branch: 'warlord', tier: 1, name: 'Rally Flag',       icon: '🚩', desc: 'Guardian damage {v}', effect: M('heroDamage', 0.5), maxLevel: 10 },
  { id: 'w_vanguard', branch: 'warlord', tier: 1, name: 'Warrior Drills',    icon: '🛡️', desc: 'Warrior Guardian damage {v}', effect: M('heroDamage.melee', 0.8), maxLevel: 10 },
  { id: 'w_marksmen', branch: 'warlord', tier: 2, name: 'Ranger Drills',    icon: '🎯', desc: 'Ranger Guardian damage {v}', effect: M('heroDamage.ranged', 0.8), maxLevel: 10 },
  { id: 'w_arcanist', branch: 'warlord', tier: 2, name: 'Mage Drills',   icon: '🪄', desc: 'Mage Guardian damage {v}', effect: M('heroDamage.spell', 0.8), maxLevel: 10 },
  { id: 'w_hymn',     branch: 'warlord', tier: 3, name: 'Marching Band', icon: '🥁', desc: 'Rally power {v}', effect: A('warcryBonus', 0.5), maxLevel: 10 },
  { id: 'w_veterans', branch: 'warlord', tier: 3, name: 'Hand-Me-Downs',    icon: '🧸', desc: 'Guardian upgrade cost {v}', effect: { kind: 'div', stat: 'heroCost', perLevel: 0.1 }, maxLevel: 10 },
  // Sorcerer
  { id: 's_well',     branch: 'sorcerer', tier: 1, name: 'Big Battery',       icon: '🔋', desc: 'Max energy {v}', effect: A('maxMana', 10), maxLevel: 10 },
  { id: 's_flow',     branch: 'sorcerer', tier: 1, name: 'Quick Wind-Up',    icon: '🗝️', desc: 'Energy regeneration {v}', effect: A('manaRegenPct', 0.1), maxLevel: 10 },
  { id: 's_storm',    branch: 'sorcerer', tier: 2, name: 'Bigger Boom',     icon: '💣', desc: 'Toy Bomb damage {v}', effect: M('strikeDamage', 0.5), maxLevel: 10 },
  { id: 's_lasting',  branch: 'sorcerer', tier: 2, name: 'Long Lasting',   icon: '⏳', desc: 'Special duration {v}', effect: A('skillDurationPct', 0.05), maxLevel: 10 },
  { id: 's_frenzy',   branch: 'sorcerer', tier: 3, name: 'Hero Hustle',   icon: '🌀', desc: 'Hero Time taps per second {v}', effect: A('frenzyRate', 2), maxLevel: 10 },
  { id: 's_resonance', branch: 'sorcerer', tier: 3, name: 'Magic Glitter', icon: '✨', desc: 'All damage {v}', effect: M('allDamage', 0.1), maxLevel: 10 },
  // Rogue
  { id: 'r_pocket',  branch: 'rogue', tier: 1, name: 'Coin Finder',    icon: '🪙', desc: 'Coins {v}', effect: M('gold', 0.3), maxLevel: 10 },
  { id: 'r_bounty',  branch: 'rogue', tier: 1, name: 'Giant Bounty',   icon: '👑', desc: 'Giant coins {v}', effect: M('bossGold', 0.5), maxLevel: 10 },
  { id: 'r_midas',   branch: 'rogue', tier: 2, name: 'Piggy Power', icon: '🐷', desc: 'Piggy Bank coins {v}', effect: M('midasGold', 0.3), maxLevel: 10 },
  { id: 'r_idle',    branch: 'rogue', tier: 2, name: 'Nap Time',    icon: '💤', desc: 'Offline efficiency {v}', effect: A('offlineEfficiency', 0.05), maxLevel: 10 },
  { id: 'r_relic',   branch: 'rogue', tier: 3, name: 'Stardust Seeker',  icon: '🌟', desc: 'Stardust from Rewinds {v}', effect: M('relicGain', 0.05), maxLevel: 10 },
  { id: 'r_thief',   branch: 'rogue', tier: 3, name: 'Extra Time',    icon: '⏰', desc: 'Giant timer {v}', effect: A('bossTimeSec', 1), maxLevel: 10 },
];

export const NODE_BY_ID: ReadonlyMap<string, TreeNode> = new Map(TREE.map((n) => [n.id, n]));
